import { Kaggle, featureFlags, kernelsClient } from "@kaggle/sdk";
import {
  LegacyListCompetitionsRequest,
  competitionsLegacyClient,
} from "@kaggle/sdk/competitions/legacy";
import {
  CompetitionListTab,
  CompetitionSortBy,
  HostSegment,
} from "@kaggle/sdk/competitions";
import { editorErrorOccurred } from "../kernels/redux/actions/editor";
import { getLanguageFromId } from "../kernels/redux/selectors/kernelDraft";
import { AdhocClient } from "./services";
import { uploadFile } from "@kaggle/components";
import { ClientBlobBucket } from "@kaggle/sdk/blobs";

/**
 * One-off custom fetch client bindings.
 *
 * Note: All routes fetched in this must be added to the `devServer.proxy`
 * node in dev webpack configs to ensure they proxy back to the right host.
 */
const client: AdhocClient = {
  viewCompetitionsList: async (
    category: string,
    page: number,
    search: string,
    sortBy: string,
    tab: string
  ) => {
    return competitionsLegacyClient.legacyCompetitionService.listCompetitions({
      tab: tab ?? CompetitionListTab.GENERAL,
      category: category ?? HostSegment.UNSPECIFIED,
      sortBy: sortBy ?? CompetitionSortBy.GROUPED,
      search,
      // Null means to attempt to infer CurrentUserId on backend
      userId: undefined,
      isProfileListing: false,
      page,
    } as LegacyListCompetitionsRequest);
  },

  saveDraft: async ({
    kernelId,
    parentKernelVersionId,
    sequence,
    dataSources,
    languageId,
    accelerator,
    isInternetEnabled,
    source,
    sourceType,
    externalSourceData,
  }) => {
    try {
      let uploadSource = source;
      let sourceToken;
      if (featureFlags.kernelsDraftUploadBlob) {
        const blobInfo = await uploadFile(
          ClientBlobBucket.USER_CONTENT,
          new File([source || ""], "__notebook__.ipynb", undefined)
        );
        uploadSource = undefined;
        sourceToken = blobInfo.token;
      }

      const response = await kernelsClient.kernelsService.updateKernelDraft({
        kernelId,
        parentKernelVersionId,
        sequence,
        settings: {
          dataSources,
          accelerator,
          isInternetEnabled,
          language: getLanguageFromId(languageId).name,
          sourceType,
          isGpuEnabled: accelerator === Kaggle.Sdk.Kernels.AcceleratorType.GPU,
        },
        source: uploadSource,
        sourceToken,
        externalSourceData: externalSourceData ?? undefined,
      });
      return {
        result: {
          ...response,
        },
        wasSuccessful: true,
      };
    } catch (err) {
      return {
        errors: [err.message],
        wasSuccessful: false,
      };
    }
  },

  sendKeepAlive: (url: string) => {
    const handleError = (message?: string) => {
      if (message) {
        editorErrorOccurred(message);
      }
    };

    return fetch(url)
      .then(response => response.json())
      .then(response => {
        if (!response.success) {
          const error = ([response.error] ||
            response.errors || [
              new Error("Unsuccessful keep alive response."),
            ])[0];
          handleError(error.message || error);
        }
      })
      .catch(e => {
        handleError(e.message);
      });
  },
};

export default client;
